import { useRef, useState } from 'react';
import { motion, useMotionValue, useReducedMotion, useSpring, useTransform } from 'framer-motion';
import { useEasedNumber } from './primitives';

/** The games we can settle, with the authority that actually calls the result. */
const SLIP_GAMES = [
  { label: 'Chess.com', api: 'Chess.com API' },
  { label: 'Valorant', api: 'Riot API' },
  { label: 'LoL', api: 'Riot API' },
  { label: 'CS2', api: 'FACEIT API' },
  { label: 'Dota 2', api: 'OpenDota API' },
  { label: 'Clash Royale', api: 'Supercell API' },
];

const STAKES = [10, 25, 50, 100, 250];

const FEE_RATE = 0.01;

const CODE_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

function makeCode() {
  let out = '';
  for (let i = 0; i < 5; i++) out += CODE_CHARS[Math.floor(Math.random() * CODE_CHARS.length)];
  return out;
}

function Row({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string;
  tone?: 'default' | 'dim' | 'acid';
}) {
  const color = tone === 'acid' ? 'var(--acid)' : tone === 'dim' ? 'var(--ink-3)' : 'var(--ink)';

  return (
    <div className="flex items-baseline justify-between gap-4 py-[9px]">
      <span className="pu-label" style={{ letterSpacing: '0.14em' }}>
        {label}
      </span>
      <span className="pu-num text-[15px] font-medium" style={{ color }}>
        {value}
      </span>
    </div>
  );
}

export function ChallengeSlip({ onSend }: { onSend: () => void }) {
  const [gameIndex, setGameIndex] = useState(0);
  const [stake, setStake] = useState(50);
  const [code] = useState(makeCode);

  const game = SLIP_GAMES[gameIndex];

  const pot = stake * 2;
  const fee = pot * FEE_RATE;
  const collect = pot - fee;

  // Figures settle rather than snap when the stake changes.
  const stakeShown = useEasedNumber(stake);
  const potShown = useEasedNumber(pot);
  const feeShown = useEasedNumber(fee);
  const collectShown = useEasedNumber(collect);

  // Cursor-reactive tilt — deliberately tiny, it should read as parallax not as a toy.
  const reduce = useReducedMotion();
  const cardRef = useRef<HTMLDivElement>(null);
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const rx = useSpring(useTransform(py, [-0.5, 0.5], [3.5, -3.5]), { stiffness: 140, damping: 18 });
  const ry = useSpring(useTransform(px, [-0.5, 0.5], [-3.5, 3.5]), { stiffness: 140, damping: 18 });

  const handlePointer = (e: React.PointerEvent) => {
    if (reduce || e.pointerType !== 'mouse' || !cardRef.current) return;
    const r = cardRef.current.getBoundingClientRect();
    px.set((e.clientX - r.left) / r.width - 0.5);
    py.set((e.clientY - r.top) / r.height - 0.5);
  };

  const resetPointer = () => {
    px.set(0);
    py.set(0);
  };

  return (
    <div style={{ perspective: 1200 }}>
      <motion.div
        ref={cardRef}
        onPointerMove={handlePointer}
        onPointerLeave={resetPointer}
        style={{
          rotateX: reduce ? 0 : rx,
          rotateY: reduce ? 0 : ry,
          transformStyle: 'preserve-3d',
          background: 'var(--surface)',
          borderColor: 'var(--line-2)',
        }}
        className="border p-5 sm:p-7"
      >
        {/* Slip header */}
        <div
          className="flex items-center justify-between pb-4"
          style={{ borderBottom: '1px solid var(--line)' }}
        >
          <div className="flex items-center gap-2.5">
            <span className="pu-dot" />
            <span className="pu-label" style={{ color: 'var(--ink)' }}>
              Challenge Slip
            </span>
          </div>
          <span className="pu-num text-[11px]" style={{ color: 'var(--ink-3)' }}>
            #{code}
          </span>
        </div>

        {/* Game selector */}
        <div className="pt-5">
          <div className="pu-label mb-3">01 — Pick the game</div>
          <div className="flex flex-wrap gap-1.5">
            {SLIP_GAMES.map((g, i) => (
              <button
                key={g.label}
                type="button"
                className="pu-chip"
                data-on={i === gameIndex}
                onClick={() => setGameIndex(i)}
              >
                {g.label}
              </button>
            ))}
          </div>
        </div>

        {/* Stake selector */}
        <div className="pt-6">
          <div className="pu-label mb-3">02 — Set the stake</div>
          <div className="flex flex-wrap gap-1.5">
            {STAKES.map((s) => (
              <button
                key={s}
                type="button"
                className="pu-chip pu-num"
                data-on={s === stake}
                onClick={() => setStake(s)}
                style={{ minWidth: 62, justifyContent: 'center' }}
              >
                ${s}
              </button>
            ))}
          </div>
        </div>

        {/* The maths */}
        <div className="mt-7 pt-1" style={{ borderTop: '1px solid var(--line)' }}>
          <Row label="Your stake" value={`$${stakeShown.toFixed(2)}`} />
          <Row label="Their stake" value={`$${stakeShown.toFixed(2)}`} />
          <div className="pu-hr my-1" />
          <Row label="Total pot" value={`$${potShown.toFixed(2)}`} />
          <Row label="Platform fee · 1%" value={`−$${feeShown.toFixed(2)}`} tone="dim" />
          <div className="my-1" style={{ height: 1, background: 'var(--line-2)' }} />
          <div className="flex items-baseline justify-between gap-4 py-2">
            <span className="pu-label" style={{ color: 'var(--ink)', letterSpacing: '0.14em' }}>
              You collect
            </span>
            <span
              className="pu-num text-[26px] font-semibold leading-none"
              style={{ color: 'var(--acid)' }}
            >
              ${collectShown.toFixed(2)}
            </span>
          </div>
        </div>

        {/* Footer */}
        <button type="button" className="pu-btn pu-btn--acid mt-6 w-full" onClick={onSend}>
          <span>Send it</span>
          <span aria-hidden>→</span>
        </button>

        <p
          className="pu-label mt-4 text-center leading-relaxed"
          style={{ letterSpacing: '0.12em' }}
        >
          Settled by {game.api} · Typically under 60s
        </p>
      </motion.div>
    </div>
  );
}
